/**
 * Drop-in replacement for @radix-ui/react-compose-refs that returns a
 * **stable** callback from useComposedRefs.
 *
 * The upstream implementation creates a new composed callback whenever any
 * input ref identity changes.  React 19 treats a changed ref callback as a
 * signal to run cleanup → setup, which can trigger state updates inside
 * Radix internals (e.g. Tooltip's setTrigger), causing an infinite
 * re-render loop (React error #185).
 *
 * Fix: store the latest refs in a mutable ref-object and return a callback
 * whose identity never changes.
 */

import { useRef, useCallback } from 'react';

function setRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') {
    return ref(value);
  } else if (ref !== null && ref !== undefined) {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

function composeRefs<T>(...refs: (React.Ref<T> | undefined)[]) {
  return (node: T | null) => {
    let hasCleanup = false;
    const cleanups = refs.map((ref) => {
      const cleanup = setRef(ref, node);
      if (!hasCleanup && typeof cleanup === 'function') {
        hasCleanup = true;
      }
      return cleanup;
    });
    if (hasCleanup) {
      return () => {
        for (let i = 0; i < cleanups.length; i++) {
          const cleanup = cleanups[i];
          if (typeof cleanup === 'function') {
            (cleanup as () => void)();
          } else {
            setRef(refs[i], null);
          }
        }
      };
    }
  };
}

function useComposedRefs<T>(...refs: (React.Ref<T> | undefined)[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const latest = useRef(refs);
  latest.current = refs;

  // Stable callback — identity never changes, so React 19 won't re-trigger
  // ref cleanup/setup on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback((node: T | null) => {
    return composeRefs(...latest.current)(node);
  }, []);
}

export { composeRefs, useComposedRefs };
